import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, ClipboardList } from 'lucide-react';
import { usePolls } from '../hooks';
import { PollCard } from '../components';
import { FavoriteButton } from '../../../components/FavoriteButton';

export function PollListPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const { polls, loading, error, hasMore, loadMore } = usePolls({ status: statusFilter });

  const filters = [
    { label: 'All', value: undefined },
    { label: 'Draft', value: 'draft' },
    { label: 'Active', value: 'active' },
    { label: 'Closed', value: 'closed' },
  ];

  return (
    <div className="max-w-3xl mx-auto pt-6 px-4 sm:px-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">My Polls</h1>
            <FavoriteButton archeId="arche_polls" />
          </div>
          <p className="text-sm text-muted-foreground mt-1">Create and manage your polls</p>
        </div>
        <Button onClick={() => navigate('/a/arche_polls/create')}>
          <Plus className="h-4 w-4 mr-2" />
          Create Poll
        </Button>
      </div>

      <div className="flex gap-1 mb-5 p-1 bg-muted/50 rounded-lg w-fit">
        {filters.map((f) => (
          <button
            key={f.label}
            onClick={() => setStatusFilter(f.value)}
            className={`
              px-3 py-1.5 rounded-md text-sm transition-colors
              ${
                statusFilter === f.value
                  ? 'bg-background text-foreground shadow-sm font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }
            `}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && polls.length === 0 ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[72px] w-full rounded-lg" />
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-destructive">{error}</p>
        </div>
      ) : polls.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <ClipboardList className="h-8 w-8 text-muted-foreground/50" />
          </div>
          {statusFilter ? (
            <>
              <h3 className="font-medium mb-1">No {statusFilter} polls</h3>
              <p className="text-sm text-muted-foreground mb-4">No polls match this filter.</p>
              <Button variant="outline" onClick={() => setStatusFilter(undefined)}>
                Show all polls
              </Button>
            </>
          ) : (
            <>
              <h3 className="font-medium mb-1">No polls yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first poll to start gathering responses
              </p>
              <Button variant="outline" onClick={() => navigate('/a/arche_polls/create')}>
                <Plus className="h-4 w-4 mr-2" />
                Create your first poll
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {polls.map((poll) => (
            <PollCard
              key={poll.pollId}
              pollId={poll.pollId}
              title={poll.title}
              status={poll.status}
              closesAt={poll.closesAt}
              createdAt={poll.createdAt}
            />
          ))}
          {hasMore && (
            <div className="flex justify-center pt-4">
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loading}>
                Load More
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
